import { useEffect, useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Airplane, Microphone, Radio, ArrowLeft, MapPin, SunHorizon } from "@phosphor-icons/react";

// Format times in Malaysia time (UTC+8) so both worker and admin see MYT
// regardless of the viewer's device timezone.
const MYT_OPTS = { timeZone: "Asia/Kuala_Lumpur" };
const myTime = (d) => new Date(d).toLocaleTimeString("en-GB", { ...MYT_OPTS, hour12: false });

// Dedicated post-punch-in screen so workers can't tap CLOCK OUT by accident.
// - Requests a Screen Wake Lock while mounted (screen stays awake).
// - Owns the walkie-talkie WebSocket, PTT recorder and 20-second GPS pings.
// - Releases the wake lock + closes the socket + stops GPS timer on unmount so
//   the phone can auto-lock again the instant the user leaves this page.
export default function WalkieScreen() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [talking, setTalking] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [audioLog, setAudioLog] = useState([]);
  const [onChannel, setOnChannel] = useState([]);
  const [receivingFrom, setReceivingFrom] = useState(null);
  const [gps, setGps] = useState(null);
  const [gpsErr, setGpsErr] = useState("");
  const [now, setNow] = useState(new Date());
  const [wakeOn, setWakeOn] = useState(false);
  const [latestPunch, setLatestPunch] = useState(null);
  const [checked, setChecked] = useState(false);
  const wsRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const audioQueueRef = useRef([]);
  const playingRef = useRef(false);
  const locTimerRef = useRef(null);
  const wakeRef = useRef(null);
  const silentAudioRef = useRef(null);

  // 1s clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // ---- Web "foreground service" trick: play a silent looping WAV and hand it
  // to the Media Session API. Android/Chrome will treat this like a music app,
  // pin a media notification, and keep the tab alive much longer when
  // backgrounded. Also drives navigator.mediaSession so the OS knows we're
  // "playing".  This is Path A — best-effort; a true foreground service
  // requires the native APK (see /app/mobile-android-plugin).
  useEffect(() => {
    // Build a 1-second 8-bit silent WAV in-memory so we don't ship an audio file.
    const sampleRate = 8000, dur = 1;
    const numSamples = sampleRate * dur;
    const buf = new ArrayBuffer(44 + numSamples);
    const dv = new DataView(buf);
    const w = (o, s) => { for (let i = 0; i < s.length; i++) dv.setUint8(o + i, s.charCodeAt(i)); };
    w(0, "RIFF"); dv.setUint32(4, 36 + numSamples, true); w(8, "WAVE");
    w(12, "fmt "); dv.setUint32(16, 16, true); dv.setUint16(20, 1, true);
    dv.setUint16(22, 1, true); dv.setUint32(24, sampleRate, true);
    dv.setUint32(28, sampleRate, true); dv.setUint16(32, 1, true); dv.setUint16(34, 8, true);
    w(36, "data"); dv.setUint32(40, numSamples, true);
    for (let i = 0; i < numSamples; i++) dv.setUint8(44 + i, 128); // silence
    const url = URL.createObjectURL(new Blob([buf], { type: "audio/wav" }));
    const audio = new Audio(url);
    audio.loop = true;
    audio.volume = 0.001; // near-silent but non-zero so some browsers keep it alive
    audio.play().catch(() => { /* autoplay blocked — user gesture needed */ });
    silentAudioRef.current = audio;

    // Wire Media Session so Android shows the persistent media notification.
    if ("mediaSession" in navigator) {
      try {
        navigator.mediaSession.metadata = new window.MediaMetadata({
          title: "Walkie-Talkie Active",
          artist: user?.name || "Shift running",
          album: "Tom Cargo",
        });
        navigator.mediaSession.playbackState = "playing";
        navigator.mediaSession.setActionHandler("play", () => audio.play().catch(() => {}));
        navigator.mediaSession.setActionHandler("pause", () => { /* Ignore — shift is active */ });
        navigator.mediaSession.setActionHandler("stop", () => navigate("/punch", { state: { fromWalkie: true } }));
      } catch (e) { /* ignore */ }
    }

    // Best-effort: kick off native foreground service when running inside Capacitor.
    // No-op on web (function returns null).
    import("@/lib/foregroundService").then((m) => m.start && m.start({
      title: "Walkie-Talkie is active",
      text: "Tap Back to Clock Out to end your shift.",
    })).catch(() => {});

    return () => {
      try { audio.pause(); } catch (e) {}
      try { URL.revokeObjectURL(url); } catch (e) {}
      silentAudioRef.current = null;
      if ("mediaSession" in navigator) {
        try {
          navigator.mediaSession.playbackState = "none";
          navigator.mediaSession.setActionHandler("play", null);
          navigator.mediaSession.setActionHandler("pause", null);
          navigator.mediaSession.setActionHandler("stop", null);
        } catch (e) {}
      }
      import("@/lib/foregroundService").then((m) => m.stop && m.stop()).catch(() => {});
    };
  }, [user, navigate]);

  // Gate: only allow when latest punch is IN. Otherwise bounce back to /punch.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/punch/my-punches", { params: { limit: 1 } });
        if (cancelled) return;
        const latest = data && data[0];
        setLatestPunch(latest);
        setChecked(true);
        if (!latest || latest.punch_type !== "in") {
          navigate("/punch", { replace: true });
        }
      } catch (e) {
        if (!cancelled) { setChecked(true); navigate("/punch", { replace: true }); }
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  // ---- Screen Wake Lock — request while mounted; re-request on visibility change.
  useEffect(() => {
    let released = false;

    const req = async () => {
      if (!("wakeLock" in navigator)) return;
      try {
        // eslint-disable-next-line no-undef
        const wl = await navigator.wakeLock.request("screen");
        if (released) { try { await wl.release(); } catch (e) {} return; }
        wakeRef.current = wl;
        setWakeOn(true);
        wl.addEventListener("release", () => setWakeOn(false));
      } catch (e) { setWakeOn(false); }
    };

    const onVisible = () => { if (document.visibilityState === "visible") req(); };
    req();
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVisible);
      const wl = wakeRef.current;
      wakeRef.current = null;
      if (wl) { try { wl.release(); } catch (e) {} }
      setWakeOn(false);
    };
  }, []);

  // ---- GPS watch (fresh location so pings are accurate)
  useEffect(() => {
    if (!navigator.geolocation) { setGpsErr("GPS not available"); return; }
    const w = navigator.geolocation.watchPosition(
      (p) => { setGps({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy }); setGpsErr(""); },
      (e) => setGpsErr(e.message || "GPS error"),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 },
    );
    return () => navigator.geolocation.clearWatch(w);
  }, []);

  // ---- Walkie-talkie WebSocket
  useEffect(() => {
    const token = (typeof localStorage !== "undefined" && localStorage.getItem("access_token"))
      || ((document.cookie.match(/(?:^|;\s*)access_token=([^;]+)/) || [])[1] || null);
    if (!token) return;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const proto = base.startsWith("https") ? "wss" : "ws";
    const url = base.replace(/^https?/, proto) + "/api/punch/live/ws?token=" + encodeURIComponent(token);
    let ws;
    try { ws = new WebSocket(url); } catch (e) { return; }
    wsRef.current = ws;
    ws.onopen = () => setWsConnected(true);
    ws.onclose = () => setWsConnected(false);
    ws.onerror = () => setWsConnected(false);
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "presence") {
          setOnChannel(msg.listeners || []);
        } else if (msg.type === "audio" && msg.data) {
          setAudioLog((p) => [{ name: msg.from_name, ts: msg.ts }, ...p].slice(0, 10));
          setReceivingFrom(msg.from_name);
          try {
            const bin = atob(msg.data);
            const u8 = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
            audioQueueRef.current.push(URL.createObjectURL(new Blob([u8], { type: msg.mime || "audio/webm" })));
            const drain = () => {
              if (playingRef.current) return;
              const src = audioQueueRef.current.shift();
              if (!src) { setReceivingFrom(null); return; }
              playingRef.current = true;
              const a = new Audio(src);
              a.onended = () => { playingRef.current = false; URL.revokeObjectURL(src); drain(); };
              a.onerror = () => { playingRef.current = false; drain(); };
              a.play().catch(() => { playingRef.current = false; drain(); });
            };
            drain();
          } catch (e) { /* ignore */ }
        }
      } catch (e) { /* ignore */ }
    };
    return () => { try { ws.close(); } catch (e) {} };
  }, []);

  // ---- Push location every 20s while mounted.
  useEffect(() => {
    const send = () => {
      if (!gps || !wsRef.current || wsRef.current.readyState !== 1) return;
      wsRef.current.send(JSON.stringify({ type: "location", lat: gps.lat, lng: gps.lng, accuracy: gps.acc }));
    };
    send();
    locTimerRef.current = setInterval(send, 20000);
    return () => { if (locTimerRef.current) clearInterval(locTimerRef.current); };
  }, [gps]);

  const startTalk = async () => {
    if (talking || !wsRef.current || wsRef.current.readyState !== 1) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus"
                 : MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
                 : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : {});
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        try { stream.getTracks().forEach((t) => t.stop()); } catch (e) {}
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        blob.arrayBuffer().then((buf) => {
          const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
          wsRef.current?.send(JSON.stringify({ type: "audio", mime: rec.mimeType || "audio/webm", data: b64 }));
        });
      };
      recRef.current = rec;
      rec.start();
      setTalking(true);
    } catch (e) { toast.error("Mic permission required"); }
  };
  const stopTalk = () => {
    if (!talking) return;
    try { recRef.current?.stop(); } catch (e) {}
    setTalking(false);
  };

  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  if (!ready) return <div className="min-h-screen flex items-center justify-center text-sm text-zinc-500 font-mono bg-zinc-950 text-white">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "punch_worker") return <Navigate to="/dashboard" replace />;
  if (!checked) return <div className="min-h-screen flex items-center justify-center text-sm text-zinc-500 font-mono bg-zinc-950 text-white">Checking status…</div>;
  if (!latestPunch || latestPunch.punch_type !== "in") return null; // navigation already in progress

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex flex-col" data-testid="walkie-screen">
      {/* Header with back-to-punch button */}
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <button
          onClick={() => navigate("/punch", { state: { fromWalkie: true } })}
          className="flex items-center gap-1.5 text-xs text-zinc-300 hover:text-white border border-zinc-700 hover:border-zinc-500 px-3 py-1.5"
          data-testid="back-to-punch-btn"
          title="Return to Punch Card to clock out"
        >
          <ArrowLeft size={14} weight="bold" /> Back to Clock Out
        </button>
        <div className="flex items-center gap-2">
          <Airplane size={16} weight="duotone" className="text-[#FF4F00]" />
          <span className="text-[10px] uppercase tracking-widest text-zinc-400">Live Shift</span>
        </div>
        <button onClick={doLogout} className="text-[10px] text-zinc-500 underline" data-testid="walkie-logout-btn">Sign out</button>
      </div>

      {/* Clock + status pills */}
      <div className="px-4 py-4 text-center border-b border-zinc-800">
        <div className="font-mono text-3xl" data-testid="walkie-clock">{myTime(now)}</div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{user.name} · Signed IN {myTime(latestPunch.timestamp)}</div>
        <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
          <span className={`text-[10px] font-mono px-2 py-1 border ${wsConnected ? "border-emerald-500 text-emerald-400" : "border-amber-500 text-amber-400"}`} data-testid="walkie-ws-status">
            <Radio size={10} className="inline mr-1" />{wsConnected ? "CHANNEL LIVE" : "CHANNEL OFFLINE"}
          </span>
          <span className={`text-[10px] font-mono px-2 py-1 border ${gps ? "border-emerald-500 text-emerald-400" : "border-amber-500 text-amber-400"}`}>
            <MapPin size={10} className="inline mr-1" />{gps ? "GPS OK" : (gpsErr || "GPS…")}
          </span>
          <span className={`text-[10px] font-mono px-2 py-1 border ${wakeOn ? "border-emerald-500 text-emerald-400" : "border-zinc-600 text-zinc-500"}`} data-testid="wake-lock-status">
            <SunHorizon size={10} className="inline mr-1" />{wakeOn ? "SCREEN AWAKE" : ("wakeLock" in navigator ? "SLEEP OK" : "N/A")}
          </span>
        </div>
      </div>

      {/* HUGE walkie-talkie button — this is what the whole screen is for */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
        <button
          onPointerDown={startTalk}
          onPointerUp={stopTalk}
          onPointerCancel={stopTalk}
          onPointerLeave={stopTalk}
          disabled={!wsConnected}
          data-testid="walkie-ptt-btn"
          className={`w-64 h-64 rounded-full flex flex-col items-center justify-center gap-2 select-none transition-all border-4 ${
            talking
              ? "bg-red-600 border-red-300 ring-8 ring-red-600/30 scale-95"
              : "bg-[#FF4F00] border-orange-300 hover:bg-[#FF6420]"
          } disabled:opacity-40`}
        >
          <Microphone size={70} weight={talking ? "fill" : "regular"} className="text-white" />
          <span className="font-display font-black text-2xl uppercase tracking-wider text-white">
            {talking ? "TALKING…" : "HOLD"}
          </span>
          <span className="text-[10px] font-mono text-white/80">
            {talking ? "release to send" : "tap and hold to talk"}
          </span>
        </button>

        {audioLog.length > 0 && (
          <div className="mt-6 text-center">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Last heard</div>
            <div className="mt-1 font-display font-black text-sm">{audioLog[0].name}</div>
            <div className="text-[10px] font-mono text-zinc-500">{myTime(audioLog[0].ts)}</div>
          </div>
        )}

        {receivingFrom && (
          <div className="mt-3 flex items-center gap-2 bg-emerald-600/90 px-3 py-1.5" data-testid="receiving-indicator">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
            <span className="text-xs font-display font-black">{receivingFrom} is talking…</span>
          </div>
        )}
      </div>

      {/* On-channel list — who else is online right now */}
      <div className="px-4 py-3 border-t border-zinc-800" data-testid="walkie-on-channel">
        <div className="flex items-center gap-2 mb-2">
          <Radio size={12} weight="fill" className="text-emerald-400" />
          <span className="text-[10px] uppercase tracking-widest text-zinc-400">On channel · {onChannel.length}</span>
        </div>
        {onChannel.length === 0 ? (
          <div className="text-[11px] text-zinc-600 font-mono">connecting…</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {onChannel.map((u, i) => {
              const self = u.user_id === user?.id;
              return (
                <span
                  key={i}
                  data-testid={`channel-member-${u.user_id}`}
                  className={`text-[10px] font-mono px-2 py-1 border ${self ? "border-[#FF4F00] text-[#FF4F00] bg-[#FF4F00]/10" : "border-emerald-500 text-emerald-400"}`}
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1" />
                  {u.name}{self ? " (you)" : ""}
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="px-4 py-3 border-t border-zinc-800 text-center text-[10px] font-mono text-zinc-500">
        This screen keeps your phone awake. Tap <b className="text-zinc-300">← Back to Clock Out</b> when your shift is done.
      </div>
    </div>
  );
}
